import React, { useEffect, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import ReactTooltip from 'react-tooltip';
import uniqBy from 'lodash/uniqBy';
import orderBy from 'lodash/orderBy';

import Select from '../../shared/reactSelect/select.component';
import Button from '../../shared/button.component';
import ConfirmDialog from '../../shared/confirmDialog.component';
import ImportErrors from './importErrors.component';

import {
  viewOptions,
} from './constants';
import { OptionsPropType } from '../../shared/propTypes/common';
import { ScenarioOptionsPropType } from './propTypes';
import {
  selectedScenarioCurrencySelector,
  selectedScenarioToCompareSelector,
  selectedScenarioViewSelector,
  currenciesOptionsSelector,
  scenariosOptionsSelector,
} from './store/selectors';
import {
  fetchScenarios,
  setSelectedCompareScenario,
  setSelectedView,
  setSelectedCurrency,
  calculateScenario,
  uploadTaggedDataFile,
} from './store/actions';
import routes from '../../routes';
import HamburgerDropdown from '../../administration/shared/hamburgerDropdown.component';
import Config from '../../config';
import { YesNoFlag } from '../../shared/types';
import { useScenariosMembersList } from '@queries/scenarios';
import {
  useCanUserEditScenario,
  useIsScenarioImportable,
  useSelectedScenarioCompanyCodes,
  useSelectedScenarioCountryTaxType,
} from './utils';
import { toSearchString } from '../../shared/urlUtils';
import TruncatableSelect from '../../shared/reactSelect/truncatableSelect.component';
import { debouncedRebuildReactTooltip } from '../../shared/debouncedRebuildReactTooltip';
import { historyPropTypes } from '../../shared/propTypes/historyPropTypes';

const useOnOptionChange = callback => useCallback(
  option => callback(option === null ? null : option.value),
  [callback]
);

const useImportLogic = ({ importFile }) => {
  const [file, setFileToImport] = useState(null);
  const [isImportDialogVisible, setIsImportDialogVisible] = useState(false);

  const onImportDialogCancel = useCallback(() => {
    setIsImportDialogVisible(false);
    setFileToImport(null);
  }, [setIsImportDialogVisible, setFileToImport]);

  const onImportDialogConfirm = useCallback(() => {
    setIsImportDialogVisible(false);
    importFile(file);
  }, [setIsImportDialogVisible, importFile, file]);

  const onImportChange = useCallback(event => {
    setIsImportDialogVisible(true);
    setFileToImport(event.target.files[0]);
  }, [setIsImportDialogVisible]);

  return {
    onImportChange,
    isImportDialogVisible,
    onImportDialogConfirm,
    onImportDialogCancel,
  };
};

const useScenarioMembersOptions = ({ scenarioId, compareScenarioId, isFetchingEnabled = true }) => {
  const {
    data: scenarioMembers,
  } = useScenariosMembersList(
    { scenarioId, scenarioEnabled: YesNoFlag.Y },
    { enabled: isFetchingEnabled }
  );

  const {
    data: compareScenarioMembers,
  } = useScenariosMembersList({
    scenarioId: compareScenarioId, scenarioEnabled: YesNoFlag.Y,
  }, { enabled: Boolean(isFetchingEnabled && compareScenarioId) });


  return useMemo(() => orderBy(uniqBy(scenarioMembers.concat(compareScenarioMembers), 'companyCode'), 'companyCode')
    .map(({ companyCode, companyCodeDescription }) => ({
      value: companyCode,
      label: `${companyCode} - ${companyCodeDescription}`,
    })), [scenarioMembers, compareScenarioMembers]);
};


const ScenarioNavbar = ({
  history,
  match: {
    params: {
      scenarioId,
    },
    path,
  },

  fetchScenarios,

  scenarioOptions,

  selectedScenarioToCompare,
  changeScenarioToCompare,

  selectedView,
  changeView,

  selectedCurrency,
  currenciesOptions,
  changeCurrency,

  showBackToMemberListButton,
  showAddMembersButton = true,

  showMembersDropdown,

  calculateScenario,
  onRefresh,

  uploadTaggedDataFile,

  exportToExcel,
  disableExporting,
}) => {
  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const scenarioMembersOptions = useScenarioMembersOptions({
    scenarioId,
    compareScenarioId: selectedScenarioToCompare,
    isFetchingEnabled: showMembersDropdown,
  });

  const companyCodes = useSelectedScenarioCompanyCodes();
  const selectedCompanyCodes = useMemo(() => companyCodes.map(companyCode => ({
    value: companyCode,
    label: scenarioMembersOptions?.find(({ value }) => value === companyCode)?.label || '',
  })), [companyCodes, scenarioMembersOptions]);

  const selectedCountryTaxTypes = useSelectedScenarioCountryTaxType();

  const navigateToManagerMembers = useCallback(() =>
    history.push(
      routes.scenarios.rules.COMPANY_CODES.replace(/:scenarioId/, scenarioId)
    ),
  [scenarioId, history]
  );

  const navigateToMembers = useCallback(() =>
    history.push(
      routes.scenarios.MEMBERS_LIST.replace(/:scenarioId/, scenarioId)
    ),
  [scenarioId, history]
  );

  const handleRefresh = useCallback(() => {
    calculateScenario(scenarioId, {
      onSuccessCallback: onRefresh,
    });
  }, [scenarioId, calculateScenario, onRefresh]);


  const baseScenarioOptions = useMemo(() =>
    selectedScenarioToCompare
      ? scenarioOptions.filter(({ value }) => value !== selectedScenarioToCompare)
      : scenarioOptions,
  [selectedScenarioToCompare, scenarioOptions]
  );

  const scenarioToCompareOptions = useMemo(
    () => scenarioOptions.filter(({ value }) => value !== Number(scenarioId)),
    [scenarioOptions, scenarioId]
  );

  useEffect(() => {
    if (Number(scenarioId) === selectedScenarioToCompare) {
      changeScenarioToCompare(null);
    }
  }, [scenarioId, selectedScenarioToCompare, changeScenarioToCompare]);

  const onBaseScenarioChange = useCallback(scenarioOption => {
    const url = path.replace(/:scenarioId/, scenarioOption.value);
    const searchParams = toSearchString({
      companyCodes: selectedCompanyCodes.map(({ value }) => value),
      countryTaxTypes: selectedCountryTaxTypes,
    });

    history.replace(`${url}?${searchParams}`);
  }, [history, path, selectedCompanyCodes, selectedCountryTaxTypes]);

  const onCompareScenarioChange = useOnOptionChange(changeScenarioToCompare);
  const onCurrencyChange = useOnOptionChange(changeCurrency);
  const onViewChange = useOnOptionChange(changeView);

  const onSelectedMemberChange = useCallback(companyCodeOption => {
    const url = path.replace(/:scenarioId/, scenarioId);
    const searchParams = toSearchString({
      companyCodes: companyCodeOption.map(({ value }) => value),
      countryTaxTypes: selectedCountryTaxTypes,
    });

    history.replace(`${url}?${searchParams}`);
  }, [history, path, scenarioId, selectedCountryTaxTypes]);

  const importFile = useCallback(file => {
    uploadTaggedDataFile({ scenarioId, file }, { onSuccessCallback: onRefresh });
  }, [uploadTaggedDataFile, scenarioId, onRefresh]);

  const {
    onImportChange,
    onImportDialogConfirm,
    onImportDialogCancel,
    isImportDialogVisible,
  } = useImportLogic({ importFile });

  const canModify = useCanUserEditScenario(scenarioId);
  const isImportable = useIsScenarioImportable(scenarioId);

  useEffect(() => {
    debouncedRebuildReactTooltip();
  }, []);

  return (
    <>
      <ReactTooltip/>
      <div className="d-flex scenario-navbar">
        <div className="flex-grow-1">
          <div className="row no-gutters page-content-container">
            <div className="d-flex" style={{ width: '100%' }}>
              <div className="col-4">
                Base scenario
                <Select
                  options={baseScenarioOptions}
                  onChange={onBaseScenarioChange}
                  value={Number(scenarioId)}
                />
              </div>
              <div className="col-4">
                Scenario to compare
                <Select
                  options={scenarioToCompareOptions}
                  onChange={onCompareScenarioChange}
                  value={selectedScenarioToCompare}
                  clearable
                />
              </div>
              <div className="col-2">
                Currency
                <Select
                  options={currenciesOptions}
                  onChange={onCurrencyChange}
                  value={selectedCurrency}
                />
              </div>
              <div className="col-2">
                View
                <Select
                  options={viewOptions}
                  onChange={onViewChange}
                  value={selectedView || (viewOptions && viewOptions[0].value)}
                />
              </div>
            </div>
          </div>
          <div className="row no-gutters page-content-container">
            <div className="d-flex col-8">
              <div className="px-6 d-flex col-12 justify-content-between align-items-center with-company-codes-select">
                {
                  showBackToMemberListButton && (
                    <Button
                      onClick={navigateToMembers}
                      className="mr-2"
                    >
                      Back to scenario
                    </Button>
                  )
                }
                {
                  showAddMembersButton && (
                    <Button
                      onClick={navigateToManagerMembers}
                      disabled={!canModify}
                      className="mr-2"
                    >
                      Manage members
                    </Button>

                  )
                }
                {window?.enableDebugMode ? (
                  <Button
                    onClick={handleRefresh}
                    disabled={!canModify}
                  >
                    Refresh/Calc
                  </Button>
                ) : null}

                {
                  showMembersDropdown && (
                    <>
                      <div className="ml-4">Company Codes</div>
                      <TruncatableSelect
                        options={scenarioMembersOptions}
                        onChange={onSelectedMemberChange}
                        value={selectedCompanyCodes}
                        multi
                        autosize
                        virtualized
                        clearable
                        removeSelected
                        selectComponentClassName="form-control ml-2"
                        placeholder="Select Company Codes..."
                      />
                    </>
                  )
                }
              </div>
            </div>
          </div>
        </div>
        {
          canModify && (
            <div className="hamburger-dropdown ml-auto text-right scenario-hamburger-dropdown">
              <HamburgerDropdown
                canModify={canModify}
                fileToImportChange={onImportChange}
                fileToImportSelect={closeDropdown => () => { // eslint-disable-line react/jsx-no-bind
                  closeDropdown();
                }}
                templateFileName={`TLS_Scenario_Template.xlsx`}
                templatePath={Config.templates.SCENARIOS}
                disableImporting={!isImportable}
                exportToExcel={exportToExcel}
                disableExporting={disableExporting}
              />
            </div>
          )
        }
      </div>

      <div className="row no-gutters page-content-container">
        <ImportErrors/>
      </div>
      <ConfirmDialog
        onCancel={onImportDialogCancel}
        onConfirm={onImportDialogConfirm}
        isVisible={isImportDialogVisible}
        message="Importing the file may not change the existing values in the report. Importing new files will not affect any previously imported values unless they are included in the new files. If you want to update the existing values in the report, you should include them in the new files that you are importing. Do you want to continue with the import?"
        title="Warning"
        dismissText="Cancel"
        submitText="OK"
      />
    </> );
};


ScenarioNavbar.propTypes = {
  history: historyPropTypes.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      scenarioId: PropTypes.string.isRequired,
    }),
    path: PropTypes.string,
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),

  fetchScenarios: PropTypes.func.isRequired,
  scenarioOptions: ScenarioOptionsPropType,

  selectedScenarioToCompare: PropTypes.string,
  changeScenarioToCompare: PropTypes.func.isRequired,

  selectedView: PropTypes.string,
  changeView: PropTypes.func.isRequired,

  selectedCurrency: PropTypes.string,
  currenciesOptions: OptionsPropType,
  changeCurrency: PropTypes.func.isRequired,

  showBackToMemberListButton: PropTypes.bool,
  showAddMembersButton: PropTypes.bool,

  calculateScenario: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,

  showMembersDropdown: PropTypes.bool,

  uploadTaggedDataFile: PropTypes.func.isRequired,

  exportToExcel: PropTypes.func,
  disableExporting: PropTypes.bool,
};

const mapStateToProps = state => ({
  scenarioOptions: scenariosOptionsSelector(state),
  selectedScenarioToCompare: selectedScenarioToCompareSelector(state),
  selectedView: selectedScenarioViewSelector(state),
  currenciesOptions: currenciesOptionsSelector(state),
  selectedCurrency: selectedScenarioCurrencySelector(state),
});

export default connect(mapStateToProps, {
  fetchScenarios,
  changeScenarioToCompare: setSelectedCompareScenario,
  changeView: setSelectedView,
  changeCurrency: setSelectedCurrency,
  calculateScenario,
  uploadTaggedDataFile,
})(withRouter(ScenarioNavbar));
